<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  role: { type: Object, default: null },
  catalogue: { type: Object, default: () => ({}) },
  scopes: { type: Array, default: () => [] },
  settingKeys: { type: Array, default: () => [] },
  defaultSettings: { type: Object, default: () => ({}) },
  roles: { type: Array, default: () => [] },
});

const TAKEOVER_ROLE_IDS = 'takeover_role_ids';
const TAKEOVER_AFTER_IDLE_MINUTES = 'takeover_after_idle_minutes';

// Minutes, matching what the backend stores. 0 switches idle takeover off.
const IDLE_OPTIONS = [
  { minutes: 0, label: 'IDLE_OFF' },
  { minutes: 30, label: 'IDLE_30M' },
  { minutes: 60, label: 'IDLE_1H' },
  { minutes: 240, label: 'IDLE_4H' },
  { minutes: 480, label: 'IDLE_8H' },
  { minutes: 1440, label: 'IDLE_1D' },
  { minutes: 4320, label: 'IDLE_3D' },
];

const emit = defineEmits(['close', 'submit']);
const { t } = useI18n();

const form = ref({
  name: '',
  description: '',
  rank: 10,
  visibility_scope: 'own',
  permissions: [],
  settings: {},
});

const isEdit = computed(() => Boolean(props.role?.id));

const allPermissions = computed(() => Object.values(props.catalogue).flat());

// A role taking over from itself is meaningless, so it is not on offer.
const takeoverRoleOptions = computed(() =>
  props.roles.filter(role => role.id !== props.role?.id)
);

const takeoverRoleIds = computed({
  get: () => form.value.settings[TAKEOVER_ROLE_IDS] || [],
  set: value => {
    form.value.settings[TAKEOVER_ROLE_IDS] = value;
  },
});

const idleMinutes = computed({
  get: () => Number(form.value.settings[TAKEOVER_AFTER_IDLE_MINUTES] || 0),
  set: value => {
    form.value.settings[TAKEOVER_AFTER_IDLE_MINUTES] = Number(value);
  },
});

watch(
  () => [props.show, props.role],
  () => {
    if (!props.show) return;
    form.value = props.role
      ? {
          name: props.role.name,
          description: props.role.description,
          rank: props.role.rank,
          visibility_scope: props.role.visibility_scope,
          permissions: [...(props.role.permissions || [])],
          settings: { ...(props.role.settings || {}) },
        }
      : {
          name: '',
          description: '',
          rank: 10,
          visibility_scope: 'own',
          permissions: [],
          settings: { ...props.defaultSettings },
        };
  },
  { immediate: true }
);

const selectAll = () => {
  form.value.permissions = [...allPermissions.value];
};
const clearAll = () => {
  form.value.permissions = [];
};

const onSubmit = () => {
  if (!form.value.name.trim()) return;
  emit('submit', { ...form.value, id: props.role?.id });
};
</script>

<template>
  <Modal :show="show" size="medium" @close="emit('close')">
    <form class="flex flex-col gap-5 p-8" @submit.prevent="onSubmit">
      <h3 class="text-base font-medium text-n-slate-12">
        {{
          isEdit
            ? t('ORG_ACCESS.ROLE_FORM.EDIT_TITLE')
            : t('ORG_ACCESS.ROLE_FORM.CREATE_TITLE')
        }}
      </h3>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('ORG_ACCESS.ROLE_FORM.NAME') }}
          <input v-model="form.name" type="text" />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('ORG_ACCESS.ROLE_FORM.RANK') }}
          <input v-model.number="form.rank" type="number" min="0" />
        </label>
      </div>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ORG_ACCESS.ROLE_FORM.DESCRIPTION') }}
        <textarea v-model="form.description" rows="2" />
      </label>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('ORG_ACCESS.ROLE_FORM.SCOPE') }}
        </span>
        <p class="text-xs text-n-slate-11">
          {{ t('ORG_ACCESS.ROLE_FORM.SCOPE_HINT') }}
        </p>
        <label
          v-for="scope in scopes"
          :key="scope"
          class="flex items-center gap-2 text-sm text-n-slate-12"
        >
          <input v-model="form.visibility_scope" type="radio" :value="scope" />
          {{ t(`ORG_ACCESS.SCOPES.${scope.toUpperCase()}`) }}
        </label>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('ORG_ACCESS.ROLE_FORM.OPTIONS') }}
        </span>
        <label
          v-for="key in settingKeys"
          :key="key"
          class="flex items-center gap-2 text-sm text-n-slate-12"
        >
          <input v-model="form.settings[key]" type="checkbox" />
          {{ t(`ORG_ACCESS.SETTINGS.${key.toUpperCase()}`) }}
        </label>
      </div>

      <div class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('ORG_ACCESS.TAKEOVER.TITLE') }}
        </span>
        <p class="text-xs text-n-slate-11">
          {{ t('ORG_ACCESS.TAKEOVER.HINT') }}
        </p>

        <span class="text-xs font-medium uppercase text-n-slate-11">
          {{ t('ORG_ACCESS.TAKEOVER.ROLES_LABEL') }}
        </span>
        <p v-if="!takeoverRoleOptions.length" class="text-xs text-n-slate-11">
          {{ t('ORG_ACCESS.TAKEOVER.NO_ROLES') }}
        </p>
        <div v-else class="grid grid-cols-1 gap-2 md:grid-cols-2">
          <label
            v-for="option in takeoverRoleOptions"
            :key="option.id"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              v-model="takeoverRoleIds"
              type="checkbox"
              :value="option.id"
            />
            {{ option.name }}
          </label>
        </div>

        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('ORG_ACCESS.TAKEOVER.IDLE_LABEL') }}
          <select v-model.number="idleMinutes">
            <option
              v-for="option in IDLE_OPTIONS"
              :key="option.minutes"
              :value="option.minutes"
            >
              {{ t(`ORG_ACCESS.TAKEOVER.${option.label}`) }}
            </option>
          </select>
        </label>
        <p class="text-xs text-n-slate-11">
          {{ t('ORG_ACCESS.TAKEOVER.IDLE_HINT') }}
        </p>
      </div>

      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ORG_ACCESS.ROLES.PERMISSIONS') }}
          </span>
          <div class="flex gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              :label="t('ORG_ACCESS.ROLE_FORM.SELECT_ALL')"
              @click="selectAll"
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              :label="t('ORG_ACCESS.ROLE_FORM.CLEAR_ALL')"
              @click="clearAll"
            />
          </div>
        </div>

        <div
          v-for="(keys, category) in catalogue"
          :key="category"
          class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak"
        >
          <span class="text-xs font-medium uppercase text-n-slate-11">
            {{ t(`ORG_ACCESS.CATEGORIES.${category.toUpperCase()}`) }}
          </span>
          <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
            <label
              v-for="key in keys"
              :key="key"
              class="flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input v-model="form.permissions" type="checkbox" :value="key" />
              {{ t(`ORG_ACCESS.PERMISSIONS.${key.toUpperCase()}`) }}
            </label>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          :label="t('ORG_ACCESS.ROLE_FORM.CANCEL')"
          @click="emit('close')"
        />
        <Button type="submit" :label="t('ORG_ACCESS.ROLE_FORM.SAVE')" />
      </div>
    </form>
  </Modal>
</template>
